import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./Addadvertisements.module.css";
import { useCreateAdvertisement } from "../../../hooks/advertisment/useAdvertismentHooks";

const Addadvertisements = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { createAdvertisement, isLoading, isSuccess, isError, error, reset } = useCreateAdvertisement();

  const [formData, setFormData] = useState({
    title: "",
    image_url: "", // Changed from imageFile to match API expectation
    link: "",
    description: "",
    category: "promotional",
    pages: {
      home: false,
      audio: false,
      reading: false,
      video: false,
      blogs: false
    },
    start_date: "",
    end_date: "",
    priority: "medium",
    active: true,
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith("page_")) {
      // Handle page checkboxes
      const pageName = name.replace("page_", "");
      setFormData((prev) => ({
        ...prev,
        pages: {
          ...prev.pages,
          [pageName]: checked
        }
      }));
    } else {
      // Handle other form fields
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  // Handle image upload and generate preview
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setImageFile(file);
      setImagePreview(previewUrl);
    }
  };

  // Remove selected image and clear file input
  const handleImageRemove = () => {
    setImageFile(null);
    setImagePreview("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle form submission
 // Modify handleSubmit to ensure all required fields are correctly formatted
const handleSubmit = async (e) => {
  e.preventDefault();

  // Validate that at least one page is selected
  const hasSelectedPage = Object.values(formData.pages).some(value => value === true);
  
  if (!hasSelectedPage) {
    toast.error("Please select at least one page for the advertisement", {
      position: "top-center",
      autoClose: 3000,
    });
    return;
  }

  // Prepare form data for upload
  const uploadData = new FormData();
  
  // Append all form fields to FormData
  Object.keys(formData).forEach(key => {
    if (key === 'pages') {
      // Convert pages object to comma-separated string of active pages
      const activePages = Object.entries(formData.pages)
        .filter(([_, value]) => value)
        .map(([key]) => key);
      uploadData.append(key, activePages.join(','));
    } else if (key !== 'image_url') {
      uploadData.append(key, formData[key]);
    }
  });

  // Append image file if exists
  if (imageFile) {
    uploadData.append('image', imageFile);
    // Optionally set image_url to the file name or a temporary URL
    uploadData.append('image_url', imageFile.name);
  }

  // Call create advertisement mutation
  createAdvertisement(uploadData);
};

  // Handle successful creation
  useEffect(() => {
    if (isSuccess) {
      // Wait and then navigate back
      const timer = setTimeout(() => {
        navigate(-1);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isSuccess, navigate]);

  // Navigate back to previous page
  const goBack = () => {
    navigate(-1);
  };

  return (
    <div className={styles.section}>
      <div className={styles.container}>
        <h2 className={styles.heading}>Add New Advertisement</h2>
        
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label className={styles.label}>
              Title:
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={styles.input}
                required
              />
            </label>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Description:
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className={styles.textarea}
                rows="3"
              />
            </label>
          </div>

           {/* Image Upload Section */}
           <div className={styles.formGroup}>
            <label className={styles.label}>
              Upload Image:
              <input
                type="file"
                name="image"
                accept="image/*"
                onChange={handleImageUpload}
                ref={fileInputRef}
                className={styles.input}
                required
              />
            </label>
            {imagePreview && (
              <div className={styles.imagePreviewContainer}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  className={styles.imagePreview}
                />
                <button
                  type="button"
                  onClick={handleImageRemove}
                  className={styles.removeImageButton}
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Link:
                <input
                  type="url"
                  name="link"
                  value={formData.link}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </label>
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Category:
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="promotional">Promotional</option>
                  <option value="featured">Featured</option>
                  <option value="sponsored">Sponsored</option>
                  <option value="partner">Partner</option>
                </select>
              </label>
            </div>
          </div>

          {/* Page Selection (Checkboxes instead of position dropdown) */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Display on Pages:</label>
            <div className={styles.checkboxGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="page_home"
                  checked={formData.pages.home}
                  onChange={handleChange}
                  className={styles.checkbox}
                />
                Home
              </label>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="page_audio"
                  checked={formData.pages.audio}
                  onChange={handleChange}
                  className={styles.checkbox}
                />
                Audio
              </label>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="page_reading"
                  checked={formData.pages.reading}
                  onChange={handleChange}
                  className={styles.checkbox}
                />
                Reading
              </label>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="page_video"
                  checked={formData.pages.video}
                  onChange={handleChange}
                  className={styles.checkbox}
                />
                Video
              </label>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="page_blogs"
                  checked={formData.pages.blogs}
                  onChange={handleChange}
                  className={styles.checkbox}
                />
                Blogs
              </label>
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Start Date:
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </label>
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.label}>
                End Date:
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                  className={styles.input}
                  min={formData.start_date || undefined}
                  required
                />
              </label>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Priority:
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className={styles.select}
                required
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </label>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
                className={styles.checkbox}
                required
              />
              Active
            </label>
          </div>

          <div className={styles.buttonContainer}>
            <button 
              type="submit" 
              className={styles.submitButton}
              disabled={isLoading}
            >
              {isLoading ? "Adding..." : "Add Advertisement"}
            </button>
            <button
              type="button"
              onClick={goBack}
              className={styles.cancelButton}
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </form>

        {/* Optional: display any submission errors */}
        {isError && (
          <div className={styles.errorMessage}>
            {error?.response?.data?.message || "Failed to add advertisement"}
          </div>
        )}
      </div>
      <ToastContainer />
    </div>
  );
};

export default Addadvertisements;